import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';

const POLines = () => {
  const [poLines, setPOLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({
    search: '',
    responseStatus: '',
    showAll: false
  });

  const fetchPOLines = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 25,
        ...(filters.search && { search: filters.search }),
        ...(filters.responseStatus && { responseStatus: filters.responseStatus }),
        ...(filters.showAll && { showAll: 'true' })
      });
      
      const response = await api.get(`/po-lines?${params}`);
      setPOLines(response.data.poLines);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('Failed to fetch PO lines:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, filters]);

  useEffect(() => {
    fetchPOLines();
  }, [fetchPOLines]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchPOLines();
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'DAY1_PENDING': 'warning',
      'DAY1_SENT': 'info',
      'DAY2_NO_RESPONSE': 'warning',
      'DAY3_NO_RESPONSE': 'danger',
      'DAY4_ESCALATE': 'danger',
      'RESPONDED_POSITIVE': 'success',
      'RESPONDED_DELAY': 'warning',
      'CLOSED': 'secondary'
    };
    return statusColors[status] || 'secondary';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 style={{ fontSize: '1.5rem', fontWeight: '600' }}>PO Lines</h1>
        <div className="text-sm text-muted">
          {!filters.showAll && 'Showing VServe Action items only'}
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div style={{ flex: 1 }}>
              <div style={{ position: 'relative' }}>
                <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input
                  type="text"
                  className="form-input"
                  style={{ paddingLeft: '36px' }}
                  placeholder="Search PO#, Item, Supplier..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
            </div>
            
            <select
              className="form-input"
              style={{ width: '200px' }}
              value={filters.responseStatus}
              onChange={(e) => setFilters(prev => ({ ...prev, responseStatus: e.target.value }))}
            >
              <option value="">All Statuses</option>
              <option value="DAY1_PENDING">Day 1 - Pending</option>
              <option value="DAY1_SENT">Day 1 - Sent</option>
              <option value="DAY2_NO_RESPONSE">Day 2 - No Response</option>
              <option value="DAY3_NO_RESPONSE">Day 3 - No Response</option>
              <option value="DAY4_ESCALATE">Day 4 - Escalate</option>
              <option value="RESPONDED_POSITIVE">Responded - Positive</option>
            </select>
            
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={filters.showAll}
                onChange={(e) => setFilters(prev => ({ ...prev, showAll: e.target.checked }))}
              />
              <span className="text-sm">Show All</span>
            </label>
            
            <button type="submit" className="btn btn-primary">
              <Filter size={16} /> Filter
            </button>
          </form>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="table-container">
          {loading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : poLines.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📋</div>
              <p>No PO lines found</p>
              {!filters.showAll && <p className="text-sm text-muted">Try enabling "Show All" to see all records</p>}
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>PO #</th>
                  <th>Line</th>
                  <th>Supplier</th>
                  <th>Item</th>
                  <th>Due Date</th>
                  <th>MRP Need Date</th>
                  <th>VServe Action</th>
                  <th>Response Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {poLines.map((line) => (
                  <tr key={line.po_line_id}>
                    <td><strong>{line.po_number}</strong></td>
                    <td>{line.line_number}</td>
                    <td>{line.supplier_name || line.supplier_code || '-'}</td>
                    <td>
                      <div>{line.item_number}</div>
                      <div className="text-sm text-muted">{line.item_description?.substring(0, 40)}</div>
                    </td>
                    <td>{formatDate(line.current_esd)}</td>
                    <td>{formatDate(line.required_date)}</td>
                    <td>
                      <span className={`badge badge-${line.vserve_action?.toLowerCase() === 'yes' ? 'success' : 'secondary'}`}>
                        {line.vserve_action || '-'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${getStatusBadge(line.response_status)}`}>
                        {line.response_status?.replace(/_/g, ' ') || 'Pending'}
                      </span>
                    </td>
                    <td>
                      <div className="text-sm" style={{ maxWidth: '200px' }}>
                        {line.modified_message_detail || '-'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
            >
              <ChevronLeft size={16} />
            </button>
            <span className="pagination-info">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} records)
            </span>
            <button
              className="pagination-btn"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page >= pagination.totalPages}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default POLines;
